import React, { useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { defineMessages, useIntl } from "react-intl";
import { defaultCrudMessages } from "../shared/crud/simple-crud";
import CrudList, { TableColumns } from "../shared/crud/list";
import { mapRenderer, NumberRenderer, StringRenderer } from "../shared/renderers/table";
import { getReservationEventsByBookingId } from "./reservationEventsApi";
import { ReservationEvent } from "./model";
import { CustomDateRenderer, CustomDateTimeRenderer, CustomEventTypeRenderer } from "./renders";
import { usePropertyConfiguration } from "../../components/property-configuration-provider";
import { ForbiddenSection } from "../../components/authorization-verifier";

interface ReservationHistoryProps {
  propertyTicker: string;
}

const tableHeaderMessages = defineMessages({
  eventType: {
    id: "reservationHistory.eventType",
    defaultMessage: "Type",
  },
  eventDate: {
    id: "reservationHistory.eventDate",
    defaultMessage: "Date",
  },
  user: {
    id: "reservationHistory.user",
    defaultMessage: "User",
  },
  multipleReservationId: {
    id: "reservationHistory.multipleReservationId",
    defaultMessage: "ID. Multi",
  },
  singleReservationId: {
    id: "reservationHistory.singleReservationId",
    defaultMessage: "Booking ID",
  },
  checkin: {
    id: "reservationHistory.checkin",
    defaultMessage: "Check-in",
  },
  checkout: {
    id: "reservationHistory.checkout",
    defaultMessage: "Check-out",
  },
  guestName: {
    id: "reservationHistory.guestName",
    defaultMessage: "Name",
  },
  guestEmail: {
    id: "reservationHistory.guestEmail",
    defaultMessage: "Email",
  },
  phone: {
    id: "reservationHistory.phone",
    defaultMessage: "Phone",
  },
  units: {
    id: "reservationHistory.units",
    defaultMessage: "Units",
  },
  inventoryTicker: {
    id: "reservationHistory.inventoryTicker",
    defaultMessage: "Inventory Line",
  },
  amount: {
    id: "reservationHistory.amount",
    defaultMessage: "Total",
  },
  deposit: {
    id: "reservationHistory.deposit",
    defaultMessage: "Deposit",
  },
  currency: {
    id: "reservationHistory.currency",
    defaultMessage: "Currency",
  },
  paymentType: {
    id: "reservationHistory.paymentType",
    defaultMessage: "Payment Type",
  },
  paymentTicker: {
    id: "reservationHistory.paymentTicker",
    defaultMessage: "Payment Ticker",
  },
  affiliate: {
    id: "reservationHistory.affiliate",
    defaultMessage: "Affiliate",
  },
  promotions: {
    id: "reservationHistory.promotions",
    defaultMessage: "Promotions",
  },
  extras: {
    id: "reservationHistory.extras",
    defaultMessage: "Services",
  },
  reservationOrigin: {
    id: "reservationHistory.reservationOrigin",
    defaultMessage: "Origin",
  },
  origin: {
    id: "reservationHistory.origin",
    defaultMessage: "O.Event",
  },
  status: {
    id: "reservationHistory.status",
    defaultMessage: "Status",
  },
  paymentStatus: {
    id: "reservationHistory.paymentStatus",
    defaultMessage: "Payment status",
  },
});

const messages = {
  ...defaultCrudMessages,
  tableHeaders: tableHeaderMessages,
  entityName: {
    id: "menu.reservations.historicalReservations",
    defaultMessage: "Historical Reservations",
  },
  subHeader: {
    id: "menu.reservations.historicalReservations.subHeader",
    defaultMessage: "Historical Reservation: {bookingId}",
  },
  RESERVATION: {
    id: "reservationHistory.reservationStatus.RESERVATION",
    defaultMessage: "Confirmed",
  },
  CANCEL: {
    id: "reservationHistory.reservationStatus.CANCEL",
    defaultMessage: "Cancelled",
  },
  PRE_RESERVATION: {
    id: "reservationHistory.reservationStatus.PRE_RESERVATION",
    defaultMessage: "Pre-booking",
  },
  REACTIVATION: {
    id: "reservationHistory.reservationStatus.REACTIVATION",
    defaultMessage: "Confirmed",
  },
  SUCCESS: {
    id: "reservationHistory.paymentStatus.SUCCESS",
    defaultMessage: "Success",
  },
  FAILED: {
    id: "reservationHistory.paymentStatus.FAILED",
    defaultMessage: "Failed",
  },
  NOT_COMPLETED: {
    id: "reservationHistory.paymentStatus.NOT_COMPLETED",
    defaultMessage: "abandoned",
  },
  PENDING: {
    id: "reservationHistory.paymentStatus.PENDING",
    defaultMessage: "Awaiting",
  },
  EXPIRED: {
    id: "reservationHistory.paymentStatus.EXPIRED",
    defaultMessage: "timed out",
  },
};

export function HistoricalReservation({ propertyTicker }: ReservationHistoryProps): JSX.Element {
  const location = useLocation();
  const intl = useIntl();
  const propertyConfiguration = usePropertyConfiguration();

  const bookingId = useMemo(() => location.pathname.split("/").pop(), [location.pathname]);

  const showHistoricalReservations = useMemo(
    () => propertyConfiguration?.showHistoricalReservations === "1",
    [propertyConfiguration]
  );

  const onGetAll = useCallback(async () => {
    if (!bookingId) {
      return;
    }
    return await getReservationEventsByBookingId(
      propertyTicker,
      bookingId,
      propertyConfiguration?.timeZone
    );
  }, [propertyTicker, bookingId, propertyConfiguration?.timeZone]);

  const tableColumns: TableColumns<ReservationEvent> = useMemo(
    () => ({
      eventType: {
        renderer: CustomEventTypeRenderer,
        width: 120,
      },
      eventDate: {
        renderer: mapRenderer(CustomDateTimeRenderer, v => v, {
          timeZone: propertyConfiguration?.timeZone,
        }),
        width: 160,
      },
      user: {
        renderer: StringRenderer,
        width: 140,
      },
      multipleReservationId: {
        renderer: StringRenderer,
        width: 100,
      },
      singleReservationId: {
        renderer: StringRenderer,
        width: 100,
      },
      status: {
        renderer: mapRenderer(StringRenderer, (v, intl) => {
          return intl.formatMessage(messages[v]);
        }),
        width: 130,
      },
      // TODO: Uncomment when backend PaymentStatus bug is fixed
      // paymentStatus: {
      //     renderer: mapRenderer(StringRenderer, (v, intl) => {
      //         return intl.formatMessage(messages[v]);
      //     }),
      //     width: 150,
      // },
      checkin: {
        renderer: CustomDateRenderer,
        width: 100,
      },
      checkout: {
        renderer: CustomDateRenderer,
        width: 100,
      },
      guestName: {
        renderer: StringRenderer,
        width: 120,
      },
      guestEmail: {
        renderer: StringRenderer,
        width: 200,
      },
      phone: {
        renderer: StringRenderer,
        width: 120,
      },
      units: {
        renderer: NumberRenderer,
        width: 80,
      },
      inventoryTicker: {
        renderer: StringRenderer,
        width: 200,
      },
      amount: {
        renderer: NumberRenderer,
        width: 80,
      },
      deposit: {
        renderer: NumberRenderer,
        width: 80,
      },
      currency: {
        renderer: StringRenderer,
        width: 80,
      },
      paymentType: {
        renderer: StringRenderer,
        width: 140,
      },
      paymentTicker: {
        renderer: StringRenderer,
        width: 140,
      },
      affiliate: {
        renderer: StringRenderer,
        width: 200,
      },
      promotions: {
        renderer: mapRenderer(StringRenderer, v => v.join(",")),
        width: 200,
      },
      extras: {
        renderer: mapRenderer(StringRenderer, v => v.join(",")),
        width: 200,
      },
      reservationOrigin: {
        renderer: StringRenderer,
        width: 120,
      },
      origin: {
        renderer: StringRenderer,
        width: 120,
      },
    }),
    [propertyConfiguration?.timeZone]
  );

  if (!showHistoricalReservations) {
    return <ForbiddenSection showMessage={true} />;
  }

  return (
    <CrudList<ReservationEvent>
      messages={messages}
      subHeader={intl.formatMessage(messages.subHeader, { bookingId })}
      columns={tableColumns}
      onGetAll={onGetAll}
      showSuccessMessage={false}
      initialHiddenColumnNames={[]}
      managerName={"reservationHistory"}
      forceLoad={false}
    />
  );
}
